import { type FormEvent, useState } from 'react';

import { login, resendVerification } from '../api/client.ts';
import { RouteLink } from '../studies/RouteLink.tsx';
import type { Route } from '../studies/routing.ts';
import { CheckEmailNotice } from './CheckEmailNotice.tsx';
import { EmailRequestForm } from './EmailRequestForm.tsx';
import { failureMessage } from './failure.ts';

export interface LoginViewProps {
  /** Called once a sign-in succeeded, so the shell can read the session it now has. */
  onSignedIn: () => void;
  /**
   * Set when a sign-in has already succeeded and the session read that followed it came back
   * unauthenticated — so this view is on screen for the second time with nothing to show for the
   * password that just worked. Left blank it reads as a form that silently did nothing, which is
   * exactly what a cookie the browser refused to keep looks like.
   */
  sessionLost?: boolean;
  /** The app's own navigation, for the links to signing up and to resetting a password. */
  onNavigate: (route: Route) => void;
}

/**
 * What the app shows when nobody is signed in.
 *
 * It holds the password only as long as the request takes and stores nothing: the session is a
 * `HttpOnly` cookie, so there is no token here to keep and nothing to clear on the way out.
 */
export const LoginView = ({ onSignedIn, sessionLost = false, onNavigate }: LoginViewProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Asking for a new confirmation link: closed, open on its form, or sent to an address. It is
   * always offered rather than prompted by a refusal, because a sign-in to an unconfirmed account
   * is refused exactly as a wrong password is — anything else would say which addresses have
   * confirmed accounts.
   */
  const [resend, setResend] = useState<
    { kind: 'closed' } | { kind: 'open' } | { kind: 'sent'; email: string }
  >({ kind: 'closed' });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);

    void login(email, password).then(
      () => {
        // Left pending: the shell replaces this view, and re-enabling the form first shows an
        // empty one for a frame.
        onSignedIn();
      },
      (failure: unknown) => {
        setPending(false);
        setPassword('');
        setError(failureMessage(failure));
      },
    );
  };

  if (resend.kind === 'sent') {
    return (
      <main className="login" data-test-id="login-view">
        <CheckEmailNotice
          email={resend.email}
          sendAgain={resendVerification}
          // "Back to sign in" is this view's own form, whichever path it is showing on.
          onNavigate={(route) => {
            setResend({ kind: 'closed' });
            onNavigate(route);
          }}
        />
      </main>
    );
  }

  return (
    <main className="login" data-test-id="login-view">
      <h1>Sign in to Sigora</h1>
      {sessionLost ? (
        <p className="login-error" data-test-id="login-session-lost" role="alert">
          That password was accepted, but the session did not stick. If this browser is reaching
          Sigora over plain http, the session cookie is being dropped.
        </p>
      ) : null}
      <form onSubmit={submit}>
        <label htmlFor="login-email">E-mail</label>
        <input
          id="login-email"
          data-test-id="login-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />

        <label htmlFor="login-password">Password</label>
        <input
          id="login-password"
          data-test-id="login-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {error === null ? null : (
          <p className="login-error" data-test-id="login-error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" data-test-id="login-submit" disabled={pending}>
          {pending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      {resend.kind === 'open' ? (
        <div data-test-id="login-resend">
          <p className="login-note">
            Enter the address you signed up with, and we will send a new confirmation link.
          </p>
          <EmailRequestForm
            testIdPrefix="resend-verification"
            initialEmail={email}
            submitLabel="Send a new link"
            pendingLabel="Sending…"
            send={resendVerification}
            onSent={(sentTo) => setResend({ kind: 'sent', email: sentTo })}
          />
        </div>
      ) : (
        <p className="login-links">
          <button
            type="button"
            data-test-id="login-resend-open"
            onClick={() => setResend({ kind: 'open' })}
          >
            Didn’t get the confirmation e-mail? Send a new link
          </button>
        </p>
      )}

      <p className="login-links">
        <RouteLink to={{ name: 'signup' }} onNavigate={onNavigate} testId="login-create-account">
          Create an account
        </RouteLink>
        <RouteLink
          to={{ name: 'forgot-password' }}
          onNavigate={onNavigate}
          testId="login-forgot-password"
        >
          Forgot your password?
        </RouteLink>
      </p>
    </main>
  );
};
